import { type FormikValues, useFormikContext } from "formik";
import { get as lodashGet } from "lodash";
import { DateTime } from "luxon";
import React, {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PillarFormInputWrapper, {
  type PillarFormInputWrapperProps,
} from "shared/components/pillar-form/components/PillarFormInputWrapper";

// Pillar form control that keeps a text input, Formik, and timezone handling in sync.
export type PillarFormDateTimeInputProps = PillarFormInputWrapperProps &
  Omit<ComponentPropsWithoutRef<"input">, "id" | "name" | "value" | "type"> & {
    // Allows this component to cover date-only, time-only, and combined inputs.
    dateTimeType: "date" | "time" | "datetime-local";
    zone?: string;
    nullable?: boolean;
    testid?: string;
  };

const DEFAULT_ZONE = "America/Chicago";

const valueHasZone = (value?: string | null): value is string => {
  // Matches offsets like Z or +05:00
  return Boolean(value && /Z$|[+-]\d{2}:\d{2}$/.test(value));
};

// Normalize any ISO-ish string into a canonical format while optionally
// forcing Luxon to interpret it in a specific zone.
const normalizeIsoValue = ({
  value,
  zone,
  interpretAsLocal = false,
  keepLocalTime = false,
}: {
  value: string;
  zone?: string;
  interpretAsLocal?: boolean;
  keepLocalTime?: boolean;
}) => {
  if (!zone) {
    return undefined;
  }

  const parseOptions = interpretAsLocal
    ? { zone }
    : valueHasZone(value)
      ? { setZone: true }
      : { zone };

  const dateTime = DateTime.fromISO(value, parseOptions);

  if (!dateTime.isValid) {
    return undefined;
  }

  const normalized = keepLocalTime
    ? dateTime.setZone(zone, { keepLocalTime: true })
    : dateTime;

  return normalized.toISO({
    includeOffset: true,
    suppressMilliseconds: true,
    suppressSeconds: true,
  });
};

const PillarFormDateTimeInput = ({
  testid,
  name,
  label,
  labelClassName,
  additionalClasses,
  className,
  onChange,
  dateTimeType,
  zone,
  nullable,
  defaultValue,
  ...props
}: PillarFormDateTimeInputProps) => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const fieldValue = lodashGet(values, name);
  const currentValue = fieldValue ?? defaultValue ?? null;
  // Determine which zone should be used for formatting and storage.
  const { zone: resolvedZone, source: zoneSource } = useMemo((): {
    zone?: string;
    source: "provided" | "value" | "default";
  } => {
    if (dateTimeType === "datetime-local") {
      if (zone) {
        return { zone, source: "provided" };
      }
      const zonedDateTime = DateTime.fromISO(currentValue, { setZone: true });
      if (zonedDateTime.isValid && valueHasZone(currentValue)) {
        // Offsets like -06:00 don't carry timezone information check if the offset matches our default zone.
        const defaultZoneDateTime = zonedDateTime.setZone(DEFAULT_ZONE);
        if (
          defaultZoneDateTime.isValid == false ||
          defaultZoneDateTime.offset !== zonedDateTime.offset
        ) {
          return { zone: zonedDateTime.zoneName, source: "value" };
        }
      }

      return { zone: DEFAULT_ZONE, source: "default" };
    }

    return { zone, source: zone ? "provided" : "default" };
  }, [currentValue, dateTimeType, zone]);

  // Store raw user keystrokes unless we absolutely need to mutate them.
  // datetime-local inputs only receive offsets when the value already has one
  // or the caller explicitly provided a zone.
  const storageZone = useMemo(() => {
    if (
      dateTimeType === "datetime-local" &&
      (zoneSource !== "default" || valueHasZone(currentValue))
    ) {
      return resolvedZone;
    }

    return undefined;
  }, [currentValue, dateTimeType, resolvedZone, zoneSource]);

  // Value that the HTML input expects for the current Formik value.
  const formattedControlValue = useMemo(() => {
    if (!currentValue) {
      return "";
    }

    const hasZone = valueHasZone(currentValue);
    const dateTime = DateTime.fromISO(currentValue, {
      zone: hasZone ? undefined : (zone ?? DEFAULT_ZONE),
      setZone: hasZone ? true : undefined,
    });

    if (!dateTime.isValid) {
      return "";
    }

    if (dateTimeType === "date") {
      return dateTime.toFormat("yyyy-MM-dd");
    }

    if (dateTimeType === "time") {
      return dateTime.toFormat("HH:mm");
    }

    const timeFormat = dateTime.millisecond
      ? "yyyy-MM-dd'T'HH:mm:ss.SSS"
      : dateTime.second
        ? "yyyy-MM-dd'T'HH:mm:ss"
        : "yyyy-MM-dd'T'HH:mm";
    return dateTime.toFormat(timeFormat);
  }, [currentValue, dateTimeType, resolvedZone]);

  // Local echo of keystrokes so the user never sees a cursor jump.
  const [localValue, setLocalValue] = useState(formattedControlValue);

  // Whenever Formik changes underneath us, sync the local echo.
  useEffect(() => {
    setLocalValue(formattedControlValue);
  }, [formattedControlValue]);

  // Keep the Formik value in lockstep with defaults, normalization, and zone overrides.
  useEffect(() => {
    if (fieldValue === undefined) {
      if (defaultValue !== undefined) {
        setFieldValue(name, defaultValue);
      }
      return;
    }

    if (dateTimeType !== "datetime-local") {
      if (formattedControlValue !== (fieldValue ?? "")) {
        setFieldValue(name, formattedControlValue);
      }
      return;
    }

    if (resolvedZone && zoneSource === "provided" && currentValue) {
      const normalizedValue = normalizeIsoValue({
        value: currentValue,
        zone: resolvedZone,
        keepLocalTime: true,
      });

      if (normalizedValue && normalizedValue !== fieldValue) {
        setFieldValue(name, normalizedValue);
      }
    }
  }, [
    currentValue,
    dateTimeType,
    defaultValue,
    fieldValue,
    formattedControlValue,
    name,
    resolvedZone,
    setFieldValue,
    zoneSource,
  ]);

  // Single onChange handler keeps Formik, local echo, and optional callbacks in sync.
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      if (rawValue === "") {
        // Respect nullable controls when clearing the input.
        setFieldValue(name, nullable ? null : undefined);
        setLocalValue("");
        onChange?.(e);
        return;
      }

      const nextValue =
        dateTimeType === "datetime-local"
          ? (normalizeIsoValue({
              value: rawValue,
              zone: storageZone,
              interpretAsLocal: true,
            }) ?? rawValue)
          : rawValue;

      setFieldValue(name, nextValue);
      setLocalValue(rawValue);
      onChange?.(e);
    },
    [
      dateTimeType,
      name,
      nullable,
      onChange,
      resolvedZone,
      setFieldValue,
      storageZone,
    ],
  );

  return (
    <PillarFormInputWrapper
      name={name}
      labelClassName={labelClassName}
      label={label}
      additionalClasses={additionalClasses ?? ""}
    >
      <input
        data-testid={testid}
        type={dateTimeType}
        className={className ?? ""}
        value={localValue}
        name={name}
        id={name}
        onChange={handleChange}
        {...props}
      />
    </PillarFormInputWrapper>
  );
};

export default PillarFormDateTimeInput;
